import React from 'react';
import {cloneDeep, find} from 'lodash';
import {Link} from 'gatsby';
import clsx from 'clsx';
import {useTranslation} from 'react-i18next';
import PropTypes from 'prop-types';

import IconContainer from './IconContainer';
import * as paginationStyles from '../../styles/layout/pagination.module.scss';
import {useMediaQuery} from '../../hooks/useMediaQuery';

const Pagination = React.memo(function Pagination ({pagination}) {
  const smartphone = useMediaQuery('(max-width: 650px)');
  const {t} = useTranslation();

  const {startPages, middlePages, endPages} = React.useMemo(() => {
    let startPages;
    let middlePages = [];
    let endPages = [];
    const limit = smartphone ? 5 : 8;
    if (pagination?.totalPages < limit) {
      startPages = cloneDeep(pagination?.pages);
    } else {
      let stop = 1;
      if (!smartphone
        || pagination?.pageIndex === 1
        || pagination?.pageIndex === pagination?.totalPages - 2) {
        stop = 2;
      }
      startPages = pagination?.pages.slice(0, stop);
      endPages = pagination?.pages.slice(pagination?.totalPages - stop, pagination?.totalPages);
      if (!find(startPages,
        (page) => page.pageIndex === pagination?.pageIndex)
          && !find(endPages, (page) => page.pageIndex === pagination?.pageIndex)) {
        const minus = smartphone ? 0 : 1;
        middlePages = pagination?.pages.slice(
          pagination?.pageIndex - minus,
          pagination?.pageIndex + stop
        );
      }
    }
    return {
      startPages: startPages, middlePages: middlePages, endPages: endPages,
    };
  }, [smartphone, pagination]);

  /* eslint-disable */
  const Figures = ({page}) => {
    return (<div
      className={pagination?.pageIndex === page?.pageIndex ?
        clsx(paginationStyles.figureDiv, paginationStyles.figureDivSelected)
        : paginationStyles.figureDiv}
      key={page}
    >
      <p className={paginationStyles.figureLink}>
        {pagination?.pageIndex === page?.pageIndex ? (`${page?.pageIndex + 1}`) : (
          <Link className={paginationStyles.link} to={page.path}>
            {page?.pageIndex + 1}{' '}
          </Link>)}
      </p>
    </div>);
  };

  const Button = ({page, label, leftIcon, rightIcon}) => {
    return (<Link to={page.path}>
      <div className={paginationStyles.buttonDiv}>
        {leftIcon && (<IconContainer color="WHITE" icon={leftIcon} size={10}/>)}
        {!smartphone && (<p
          className={paginationStyles.buttonLink}
        >
          {label}
        </p>)}
        {rightIcon && (<IconContainer color="WHITE" icon={rightIcon} size={10}/>)}
      </div>
    </Link>);
  };
  /* eslint-enable */

  return (
    <div className={paginationStyles.rootDiv}>
      {pagination?.prevPage && (
        <Button
          label={t('pagination.previous')}
          leftIcon="ARROW_LEFT_SMALL"
          page={pagination?.prevPage}
        />)}
      {startPages?.map((page, index) => (
        <Figures key={index}
          page={page}
        />))}
      {middlePages?.length > 0 && (
        <>
          <h6 style={{marginRight: 6}}>
          ...
          </h6>
          {middlePages?.map((page, index) => (
            <Figures key={index}
              page={page}
            />
          ))}
        </>
      )}
      {endPages?.length > 0 && (
        <>
          <p className={paginationStyles.paginationH6}>
                ...
          </p>
          {endPages?.map((page, index) => (
            <Figures key={index}
              page={page}
            />))}
        </>)}
      {pagination?.nextPage && (
        <Button
          label={t('pagination.next')}
          page={pagination?.nextPage}
          rightIcon="ARROW_RIGHT_SMALL"
        />
      )}
    </div>);
});

Pagination.propTypes = {
  pagination: PropTypes.shape({
    totalPages: PropTypes.number,
    pageIndex: PropTypes.number,
    pages: PropTypes.arrayOf(
      PropTypes.shape({
        pageIndex: PropTypes.number,
        path: PropTypes.string,
      })
    ),
    prevPage: PropTypes.shape({
      pageIndex: PropTypes.number,
      path: PropTypes.string,
    }),
    nextPage: PropTypes.shape({
      pageIndex: PropTypes.number,
      path: PropTypes.string,
    }),
  }),
};

export default Pagination;
